/**
 * orchardDkgCeremony - N-party Pedersen DKG for Orchard FROST.
 *
 * This is the "proper properly" distributed key generation ceremony —
 * no trusted dealer, no single party ever sees the full secret
 * polynomial. It is a port of the 3-round FROST paper DKG (Figure 1)
 * over a relay-based message bus, using the `DkgParticipant` WASM
 * class which keeps all round-secret state inside the Rust allocation.
 *
 * Ceremony overview (T-of-N, e.g. 2-of-3):
 *   1. Initiator (any vault device) creates a session and shows a QR.
 *   2. Each other vault device scans the QR and sends `dkg-join` with
 *      its Vultisig `localPartyId`.
 *   3. Once all N-1 joiners are in, initiator broadcasts `dkg-start`
 *      with the assigned FROST identifiers + T/N.
 *   4. All N parties run DKG round 1 → broadcast packages.
 *   5. All N parties run DKG round 2 → P2P packages (one per recipient).
 *   6. All N parties run DKG round 3 → derive their own KeyPackage +
 *      the shared PublicKeyPackage.
 *   7. Each party stores its share and (optionally) exports it back
 *      into its .vult file via `wrapVaultWithFrost`.
 *
 * This module intentionally does NOT touch the existing 2-party
 * signing path in `cosignerRelay.ts`. It uses the same `CosignTransport`
 * interface (via HTTP relay transport only — WebRTC is strictly
 * pairwise and unsuitable for multi-party ceremonies) so the underlying
 * relay server does not need any changes.
 *
 * Wire format: each message is `base64( AES-GCM( deflate-raw( json ) ) )`
 * matching the v2 envelope used elsewhere.
 */

import {
	createDkgParticipant,
	parseDkgRound3Result,
	isProperDkgAvailable,
	generateOrchardExtras as generateOrchardExtrasFn,
	type DkgParticipantHandle,
	type DkgOutcome,
	type OrchardKeyShare,
	type OrchardPublicKeyPackage,
} from '../components/toolbox/zcash-extensions/frozt/orchardFrostBridge';
import { createTransport, type CosignTransport } from './cosignTransports';
import { generateEntropyBytes } from './secureEntropy';

// ── Constants ────────────────────────────────────────────────────────

const DKG_QR_PREFIX = 'WB32DKG';
const DKG_PROTOCOL_VERSION = 1;
// The WinBit32 cosign relay works without the extra party-registration
// dance that Vultisig's api.vultisig.com/router requires for keysign
// sessions. Our DKG ceremony only needs a dumb message-passing relay
// keyed by sessionId + recipient role, so we default to the same
// cosign.winbit32.com server the existing WB32COSIGN flow uses — this
// fixes the "stuck on Announcing join to initiator" timeout that
// happens when we accidentally point at the Vultisig relay.
const DEFAULT_RELAY_URL = 'https://cosign.winbit32.com';

/** Polling interval while waiting for relay messages in active phases. */
const POLL_ACTIVE_MS = 600;
/** Hard ceiling for the whole ceremony; a real DKG should complete in
 *  under a minute even on slow mobile connections. */
const CEREMONY_TIMEOUT_MS = 10 * 60 * 1000;

// ── Message types ────────────────────────────────────────────────────

interface DkgJoinMsg {
	type: 'dkg-join';
	partyId: string;
	vaultUid?: string;
}
interface DkgStartMsg {
	type: 'dkg-start';
	/** Map of partyId → hex-encoded 32-byte FROST Identifier. */
	identifiers: Record<string, string>;
	maxSigners: number;
	minSigners: number;
	vaultUid?: string;
}
/**
 * Post-DKG broadcast from the initiator carrying the 64-byte (nk‖rivk)
 * Orchard extras blob that every device MUST persist verbatim. Sent
 * once the initiator has completed round 3 and knows the real
 * group_public (which `orchard_random_extras` needs as `ak` for
 * validity). Without this message each device would generate its own
 * random extras, producing divergent Orchard UFVKs: funds received at
 * one device's UA are unspendable elsewhere because the PCZT's action
 * `ak`s derive from a different FVK than the FROST `group_public`.
 */
interface DkgExtrasMsg {
	type: 'dkg-extras';
	from: string;
	/** 64-byte hex (nk‖rivk) produced by `orchard_random_extras`. */
	orchardExtras: string;
}
interface DkgRound1Msg {
	type: 'dkg-r1';
	from: string;          // sender partyId
	package: string;       // round1 package JSON (stringified inside)
}
interface DkgRound2Msg {
	type: 'dkg-r2';
	from: string;          // sender partyId
	/** Round 2 package JSON (object, not stringified). */
	package: { secret_share: string };
}
interface DkgDoneMsg {
	type: 'dkg-done';
	partyId: string;
	/** Hex of the group public key the sender derived — used for
	 *  sanity check by the initiator to detect any desync. */
	groupPublic: string;
}
interface DkgCompleteMsg { type: 'dkg-complete' }
interface DkgErrorMsg { type: 'dkg-error'; message: string; from?: string }

// ── Trusted-dealer ceremony message types ────────────────────────────
// These piggy-back on the same relay transport and encryption envelope
// as the DKG messages. The trusted-dealer ceremony is a one-round
// exchange: initiator generates all shares locally, then sends each
// joiner their assigned share over the encrypted relay and waits for
// an ack. The initiator momentarily holds all shares — that's the
// "a little less secure" trade-off the user flags up in the wizard.
interface TdSharePackageMsg {
	type: 'td-share';
	from: string;
	toPartyId: string;
	share: OrchardKeyShare;
	publicKeyPackage: OrchardPublicKeyPackage;
	orchardExtras?: string;
	minSigners: number;
	maxSigners: number;
	participantPartyIds: string[];
}
interface TdAckMsg { type: 'td-ack'; from: string }

type DkgMsg =
	| DkgJoinMsg
	| DkgStartMsg
	| DkgRound1Msg
	| DkgRound2Msg
	| DkgDoneMsg
	| DkgCompleteMsg
	| DkgErrorMsg
	| DkgExtrasMsg
	| TdSharePackageMsg
	| TdAckMsg;

// ── Session + QR helpers ─────────────────────────────────────────────

export interface DkgSessionParams {
	sessionId: string;
	encKeyHex: string;
	relayUrl: string;
}

/**
 * Extra metadata shipped in the QR payload alongside `DkgSessionParams`
 * so joiners know who to address their `dkg-join` at and can verify
 * they are joining the intended vault before touching crypto.
 */
export interface DkgQrExtras {
	initiatorPartyId: string;
	vaultUid?: string;
	minSigners?: number;
	maxSigners?: number;
	/**
	 * Ceremony mode encoded in the QR:
	 *   'dkg' — full Pedersen DKG (default; no trusted dealer)
	 *   'td'  — trusted-dealer: initiator generates all shares and
	 *           distributes them over the encrypted relay in one round
	 */
	mode?: 'dkg' | 'td';
}

// As in locketCourier and moneroDkgCeremony: `encKeyHex` guards the DKG
// round messages, which carry share material, so it takes the hardened
// path — health-checked, and refusing rather than degrading if the
// generator has failed.
function randomHex(lenBytes: number): string {
	const bytes = generateEntropyBytes(lenBytes);
	return Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('');
}

export function createDkgSession(relayUrl?: string): DkgSessionParams {
	return {
		sessionId: randomHex(16),
		encKeyHex: randomHex(32),
		relayUrl: relayUrl || DEFAULT_RELAY_URL,
	};
}

function base64urlEncode(bytes: Uint8Array): string {
	let bin = '';
	for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
	return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function base64urlDecode(s: string): Uint8Array {
	let b64 = s.replace(/-/g, '+').replace(/_/g, '/');
	while (b64.length % 4) b64 += '=';
	const bin = atob(b64);
	const out = new Uint8Array(bin.length);
	for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
	return out;
}

/**
 * Encodes the DKG session params + extras into a compact QR payload of
 * the form `WB32DKG:<version>:<base64url-json>`. JSON is used (rather
 * than fixed-position colons) so we can safely embed a Vultisig party
 * ID without worrying about characters in the `localPartyId`.
 */
export function generateDkgQR(params: DkgSessionParams, extras: DkgQrExtras): string {
	if (!extras?.initiatorPartyId) {
		throw new Error('generateDkgQR requires extras.initiatorPartyId');
	}
	const payload = {
		s: params.sessionId,
		k: params.encKeyHex,
		r: params.relayUrl,
		i: extras.initiatorPartyId,
		v: extras.vaultUid || undefined,
		t: extras.minSigners || undefined,
		n: extras.maxSigners || undefined,
		m: extras.mode && extras.mode !== 'dkg' ? extras.mode : undefined,
	};
	const bytes = new TextEncoder().encode(JSON.stringify(payload));
	return `${DKG_QR_PREFIX}:${DKG_PROTOCOL_VERSION}:${base64urlEncode(bytes)}`;
}

export function parseDkgQR(
	qr: string,
): (DkgSessionParams & DkgQrExtras) | null {
	if (!qr.startsWith(`${DKG_QR_PREFIX}:`)) return null;
	const parts = qr.split(':');
	if (parts.length < 3) return null;
	if (parseInt(parts[1], 10) !== DKG_PROTOCOL_VERSION) return null;
	try {
		const payloadB64 = parts.slice(2).join(':');
		const json = new TextDecoder().decode(base64urlDecode(payloadB64));
		const obj = JSON.parse(json);
		if (!obj?.s || !obj?.k || !obj?.r || !obj?.i) return null;
		const modeRaw = obj.m ? String(obj.m) : 'dkg';
		const mode: 'dkg' | 'td' = modeRaw === 'td' ? 'td' : 'dkg';
		return {
			sessionId: String(obj.s),
			encKeyHex: String(obj.k),
			relayUrl: String(obj.r),
			initiatorPartyId: String(obj.i),
			vaultUid: obj.v ? String(obj.v) : undefined,
			minSigners: Number.isFinite(Number(obj.t)) ? Number(obj.t) : undefined,
			maxSigners: Number.isFinite(Number(obj.n)) ? Number(obj.n) : undefined,
			mode,
		};
	} catch {
		return null;
	}
}

// ── Crypto helpers (AES-GCM envelope, matching the cosigner relay) ──

function hexToBytes(hex: string): Uint8Array {
	const clean = hex.startsWith('0x') ? hex.slice(2) : hex;
	const out = new Uint8Array(clean.length / 2);
	for (let i = 0; i < out.length; i++) out[i] = parseInt(clean.slice(i * 2, i * 2 + 2), 16);
	return out;
}

function toBase64(bytes: Uint8Array): string {
	let s = '';
	for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
	return btoa(s);
}

function fromBase64(b64: string): Uint8Array {
	const bin = atob(b64);
	const out = new Uint8Array(bin.length);
	for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
	return out;
}

async function importEncKey(hexKey: string): Promise<CryptoKey> {
	return crypto.subtle.importKey('raw', hexToBytes(hexKey), 'AES-GCM', false, ['encrypt', 'decrypt']);
}

async function deflateBytes(input: Uint8Array): Promise<Uint8Array> {
	const stream = new Blob([input]).stream().pipeThrough(new CompressionStream('deflate-raw'));
	return new Uint8Array(await new Response(stream).arrayBuffer());
}

async function inflateBytes(input: Uint8Array): Promise<Uint8Array> {
	const stream = new Blob([input]).stream().pipeThrough(new DecompressionStream('deflate-raw'));
	return new Uint8Array(await new Response(stream).arrayBuffer());
}

async function sendMsg(
	transport: CosignTransport,
	key: CryptoKey,
	from: string,
	to: string,
	msg: DkgMsg,
): Promise<void> {
	const payload = new TextEncoder().encode(JSON.stringify(msg));
	const compressed = await deflateBytes(payload);
	const iv = crypto.getRandomValues(new Uint8Array(12));
	const ct = new Uint8Array(
		await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, compressed),
	);
	const combined = new Uint8Array(iv.length + ct.length);
	combined.set(iv);
	combined.set(ct, iv.length);
	await transport.send(from, to, toBase64(combined));
}

async function receiveMsgs(
	transport: CosignTransport,
	key: CryptoKey,
	myRole: string,
): Promise<DkgMsg[]> {
	const raws = await transport.receive(myRole);
	const out: DkgMsg[] = [];
	for (const enc of raws) {
		try {
			const combined = fromBase64(enc);
			const iv = combined.slice(0, 12);
			const ciphertext = combined.slice(12);
			const plain = new Uint8Array(
				await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, ciphertext),
			);
			const json = new TextDecoder().decode(await inflateBytes(plain));
			out.push(JSON.parse(json) as DkgMsg);
		} catch (err) {
			console.warn(`[dkg-ceremony] dropped undecodable message for role=${myRole}: ${(err as Error).message}`);
		}
	}
	return out;
}

/**
 * Inbox buffer that bridges the gap between asymmetric relay polls and
 * the synchronous round-by-round DKG protocol. The HTTP relay drains
 * messages on GET, so if we only look for `dkg-r1` during round 1 and
 * the peer has already moved on and sent us `dkg-r2`, that r2 packet
 * disappears from the relay and round 2 deadlocks.
 *
 * `CeremonyInbox` keeps every decrypted message in a FIFO until the
 * matching round consumes it via `take(predicate)`. Error messages are
 * surfaced to `errorSink` the moment they arrive so the ceremony can
 * abort promptly instead of waiting for a round that will never come.
 */
class CeremonyInbox {
	private buffer: DkgMsg[] = [];

	async drain(
		transport: CosignTransport,
		key: CryptoKey,
		myRole: string,
	): Promise<void> {
		const fresh = await receiveMsgs(transport, key, myRole);
		if (fresh.length) this.buffer.push(...fresh);
	}

	take(predicate: (m: DkgMsg) => boolean): DkgMsg[] {
		const taken: DkgMsg[] = [];
		const kept: DkgMsg[] = [];
		for (const m of this.buffer) {
			if (predicate(m)) taken.push(m);
			else kept.push(m);
		}
		this.buffer = kept;
		return taken;
	}

	/**
	 * Take all `dkg-error` messages and throw the first one, so every
	 * polling loop converts peer-reported failures into a thrown error
	 * instead of deadlocking on the next-round wait.
	 */
	throwOnError(): void {
		const errs = this.take(
			(m): m is DkgErrorMsg => m.type === 'dkg-error',
		) as DkgErrorMsg[];
		if (errs.length) {
			const first = errs[0];
			throw new Error(`Peer ${first.from || 'unknown'} reported: ${first.message}`);
		}
	}
}

function checkAborted(signal?: AbortSignal): void {
	if (signal?.aborted) throw new Error('DKG ceremony aborted');
}

// ── Identifier assignment ────────────────────────────────────────────

/**
 * Deterministically assigns FROST identifiers (non-zero 32-byte scalars)
 * to each partyId, sorted lexically so all participants independently
 * agree on the same mapping before any message exchange.
 *
 * Uses 0x01, 0x02, ... 0xN big-endian — cheap, proven (matches the
 * `IdentifierList::Default` path used by `keygen_with_dealer`) and
 * well-tested by our native DKG sanity test.
 */
export function assignIdentifiers(partyIds: string[]): Record<string, string> {
	if (partyIds.length < 2) throw new Error('Need at least 2 parties for DKG');
	if (partyIds.length > 255) throw new Error('More than 255 parties not supported');
	const sorted = [...partyIds].sort();
	const out: Record<string, string> = {};
	sorted.forEach((pid, i) => {
		const id = i + 1;
		out[pid] = id.toString(16).padStart(2, '0') + '00'.repeat(31);
	});
	return out;
}

// ── Result type ──────────────────────────────────────────────────────

export interface DkgCeremonyResult {
	myPartyId: string;
	myIdentifier: string;
	share: OrchardKeyShare;
	publicKeyPackage: OrchardPublicKeyPackage;
	minSigners: number;
	maxSigners: number;
	participantPartyIds: string[];
	/**
	 * The nk‖rivk hex blob that every party in this ceremony agreed on
	 * (generated once by the initiator, broadcast via `dkg-start`). All
	 * devices MUST persist this verbatim so the merged Orchard UFVK is
	 * identical everywhere. Undefined only when the initiator could not
	 * generate extras (WASM helper missing) — downstream code must
	 * refuse to store such a bundle.
	 */
	orchardExtras?: string;
}

export interface DkgCeremonyCallbacks {
	onProgress?: (phase: string) => void;
	onPartyJoined?: (partyId: string, joinedCount: number, expected: number) => void;
	signal?: AbortSignal;
}

// ── Initiator orchestrator ──────────────────────────────────────────

export interface RunInitiatorDkgParams {
	session: DkgSessionParams;
	myPartyId: string;
	otherPartyIds: string[];
	minSigners: number;
	vaultUid?: string;
	callbacks?: DkgCeremonyCallbacks;
	/** Optional pre-constructed transport for tests. Production uses
	 *  HTTP relay only (WebRTC is pairwise and unsuitable here). */
	transport?: CosignTransport;
}

/**
 * Runs the DKG ceremony from the initiator's side. Returns once all
 * parties have confirmed they derived a consistent group public key.
 *
 * The caller is responsible for:
 *   - showing the QR (via `generateDkgQR(params.session)`)
 *   - persisting the result to storage/the .vult file (see
 *     `orchardFrostVaultIntegration.ts`)
 *   - aborting via `callbacks.signal` if the user cancels
 */
export async function runInitiatorDkgCeremony(
	params: RunInitiatorDkgParams,
): Promise<DkgCeremonyResult> {
	if (!isProperDkgAvailable()) {
		throw new Error('Proper DKG not supported: update the orchard-frost WASM bundle.');
	}
	const { session, myPartyId, otherPartyIds, minSigners, vaultUid, callbacks } = params;
	const allPartyIds = [myPartyId, ...otherPartyIds];
	if (new Set(allPartyIds).size !== allPartyIds.length) {
		throw new Error('Duplicate partyId in DKG participant list');
	}
	const maxSigners = allPartyIds.length;
	if (minSigners < 2) throw new Error('minSigners must be >= 2');
	if (minSigners > maxSigners) throw new Error('minSigners must be <= maxSigners');

	const key = await importEncKey(session.encKeyHex);
	const transport = params.transport
		?? createTransport('relay', session.sessionId, session.relayUrl);
	const inbox = new CeremonyInbox();

	const deadline = Date.now() + CEREMONY_TIMEOUT_MS;

	try {
		// Phase A: wait for every other party to join.
		callbacks?.onProgress?.(`Waiting for ${otherPartyIds.length} device(s) to join DKG ceremony...`);
		const joined = new Set<string>();
		while (joined.size < otherPartyIds.length) {
			checkAborted(callbacks?.signal);
			if (Date.now() > deadline) {
				throw new Error('DKG ceremony timed out waiting for joiners');
			}
			await inbox.drain(transport, key, myPartyId);
			inbox.throwOnError();
			const joins = inbox.take(
				(m): m is DkgJoinMsg => m.type === 'dkg-join',
			) as DkgJoinMsg[];
			for (const m of joins) {
				if (!otherPartyIds.includes(m.partyId)) {
					console.warn(`[dkg-ceremony] ignoring join from unknown partyId=${m.partyId}`);
					continue;
				}
				if (!joined.has(m.partyId)) {
					joined.add(m.partyId);
					callbacks?.onPartyJoined?.(m.partyId, joined.size, otherPartyIds.length);
				}
			}
			if (joined.size < otherPartyIds.length) {
				await new Promise(r => setTimeout(r, POLL_ACTIVE_MS));
			}
		}

		// Phase B: broadcast start message with identifier assignments.
		const identifiers = assignIdentifiers(allPartyIds);
		const startMsg: DkgStartMsg = {
			type: 'dkg-start',
			identifiers,
			maxSigners,
			minSigners,
			vaultUid,
		};
		for (const pid of otherPartyIds) {
			await sendMsg(transport, key, myPartyId, pid, startMsg);
		}
		callbacks?.onProgress?.('All devices joined. Starting DKG round 1...');

		return await runDkgRounds(
			transport,
			key,
			myPartyId,
			identifiers,
			minSigners,
			maxSigners,
			otherPartyIds,
			callbacks,
			deadline,
			inbox,
			/* isInitiator */ true,
		);
	} finally {
		try { transport.close(); } catch { /* transport may already be closed */ }
	}
}

// ── Joiner orchestrator ─────────────────────────────────────────────

export interface RunJoinerDkgParams {
	session: DkgSessionParams;
	myPartyId: string;
	/** Party ID of the initiator (needed to address join + round1 broadcast). */
	initiatorPartyId: string;
	vaultUid?: string;
	callbacks?: DkgCeremonyCallbacks;
	transport?: CosignTransport;
}

/**
 * Runs the DKG ceremony from a joiner's (cosigner's) side. The joiner
 * sends `dkg-join`, waits for `dkg-start`, then runs the same 3-round
 * protocol as the initiator.
 */
export async function runJoinerDkgCeremony(
	params: RunJoinerDkgParams,
): Promise<DkgCeremonyResult> {
	if (!isProperDkgAvailable()) {
		throw new Error('Proper DKG not supported: update the orchard-frost WASM bundle.');
	}
	const { session, myPartyId, initiatorPartyId, vaultUid, callbacks } = params;
	const key = await importEncKey(session.encKeyHex);
	const transport = params.transport
		?? createTransport('relay', session.sessionId, session.relayUrl);
	const inbox = new CeremonyInbox();

	const deadline = Date.now() + CEREMONY_TIMEOUT_MS;

	try {
		callbacks?.onProgress?.('Announcing join to initiator...');
		await sendMsg(transport, key, myPartyId, initiatorPartyId, {
			type: 'dkg-join', partyId: myPartyId, vaultUid,
		});

		callbacks?.onProgress?.('Waiting for DKG start message...');
		let start: DkgStartMsg | null = null;
		while (!start) {
			checkAborted(callbacks?.signal);
			if (Date.now() > deadline) throw new Error('DKG ceremony timed out waiting for start');
			await inbox.drain(transport, key, myPartyId);
			inbox.throwOnError();
			const starts = inbox.take(
				(m): m is DkgStartMsg => m.type === 'dkg-start',
			) as DkgStartMsg[];
			if (starts.length) { start = starts[0]; break; }
			await new Promise(r => setTimeout(r, POLL_ACTIVE_MS));
		}

		if (!start.identifiers[myPartyId]) {
			throw new Error(`Initiator did not assign an identifier for partyId=${myPartyId}`);
		}
		const allPartyIds = Object.keys(start.identifiers);
		const otherPartyIds = allPartyIds.filter(p => p !== myPartyId);

		callbacks?.onProgress?.('DKG round 1...');
		return await runDkgRounds(
			transport,
			key,
			myPartyId,
			start.identifiers,
			start.minSigners,
			start.maxSigners,
			otherPartyIds,
			callbacks,
			deadline,
			inbox,
			/* isInitiator */ false,
		);
	} finally {
		try { transport.close(); } catch { /* */ }
	}
}

// ── Shared 3-round runner ───────────────────────────────────────────

async function runDkgRounds(
	transport: CosignTransport,
	key: CryptoKey,
	myPartyId: string,
	identifiers: Record<string, string>,
	minSigners: number,
	maxSigners: number,
	otherPartyIds: string[],
	callbacks: DkgCeremonyCallbacks | undefined,
	deadline: number,
	inbox: CeremonyInbox,
	isInitiator: boolean,
): Promise<DkgCeremonyResult> {
	const myIdentifier = identifiers[myPartyId];
	if (!myIdentifier) throw new Error(`No identifier assigned for ${myPartyId}`);

	const participant = createDkgParticipant(myIdentifier, maxSigners, minSigners);
	try {
		// ── Round 1: everyone broadcasts their commitment+PoK package ──
		callbacks?.onProgress?.('DKG round 1: broadcasting commitment...');
		const r1Json = participant.round1();
		for (const pid of otherPartyIds) {
			await sendMsg(transport, key, myPartyId, pid, {
				type: 'dkg-r1', from: myPartyId, package: r1Json,
			});
		}

		// Collect round1 packages from all others, keyed by their identifier.
		// Uses the shared inbox buffer so round-2/round-3 packets that
		// arrived early (because a peer moved on while we were still
		// polling) stay put until their round is ready to consume them.
		const round1ByIdentifier: Record<string, any> = {};
		while (Object.keys(round1ByIdentifier).length < otherPartyIds.length) {
			checkAborted(callbacks?.signal);
			if (Date.now() > deadline) throw new Error('DKG round 1 timeout');
			await inbox.drain(transport, key, myPartyId);
			inbox.throwOnError();
			const r1s = inbox.take(
				(m): m is DkgRound1Msg =>
					m.type === 'dkg-r1' && !!identifiers[m.from] && m.from !== myPartyId,
			) as DkgRound1Msg[];
			for (const m of r1s) {
				const idHex = identifiers[m.from];
				if (!round1ByIdentifier[idHex]) {
					round1ByIdentifier[idHex] = JSON.parse(m.package);
				}
			}
			if (Object.keys(round1ByIdentifier).length < otherPartyIds.length) {
				await new Promise(r => setTimeout(r, POLL_ACTIVE_MS));
			}
		}
		callbacks?.onProgress?.('DKG round 1 complete. Running round 2...');

		// ── Round 2: per-recipient packages ──
		const round2OutgoingJson = participant.round2(JSON.stringify(round1ByIdentifier));
		const round2OutgoingByIdHex = JSON.parse(round2OutgoingJson) as Record<string, { secret_share: string }>;

		// Reverse lookup: identifier → partyId, for addressing
		const identifierToPartyId: Record<string, string> = {};
		for (const [pid, id] of Object.entries(identifiers)) identifierToPartyId[id] = pid;

		for (const [recipientIdHex, pkg] of Object.entries(round2OutgoingByIdHex)) {
			const recipientPartyId = identifierToPartyId[recipientIdHex];
			if (!recipientPartyId) {
				throw new Error(`round2 produced package for unknown identifier ${recipientIdHex}`);
			}
			await sendMsg(transport, key, myPartyId, recipientPartyId, {
				type: 'dkg-r2', from: myPartyId, package: pkg,
			});
		}

		// Collect round2 packages addressed to me from every other party.
		const round2ForMe: Record<string, { secret_share: string }> = {};
		while (Object.keys(round2ForMe).length < otherPartyIds.length) {
			checkAborted(callbacks?.signal);
			if (Date.now() > deadline) throw new Error('DKG round 2 timeout');
			await inbox.drain(transport, key, myPartyId);
			inbox.throwOnError();
			const r2s = inbox.take(
				(m): m is DkgRound2Msg =>
					m.type === 'dkg-r2' && !!identifiers[m.from] && m.from !== myPartyId,
			) as DkgRound2Msg[];
			for (const m of r2s) {
				const idHex = identifiers[m.from];
				if (!round2ForMe[idHex]) round2ForMe[idHex] = m.package;
			}
			if (Object.keys(round2ForMe).length < otherPartyIds.length) {
				await new Promise(r => setTimeout(r, POLL_ACTIVE_MS));
			}
		}
		callbacks?.onProgress?.('DKG round 2 complete. Finalising...');

		// ── Round 3: derive our key + group public ──
		const round3ResultJson = participant.round3(JSON.stringify(round2ForMe));
		const outcome: DkgOutcome = parseDkgRound3Result(round3ResultJson);
		callbacks?.onProgress?.('DKG complete; verifying consistency...');

		// Sanity check: my identifier must match what I was assigned.
		if (outcome.share.id.toLowerCase() !== myIdentifier.toLowerCase()) {
			throw new Error(
				`DKG produced wrong identifier: got ${outcome.share.id}, expected ${myIdentifier}`,
			);
		}
		// Sanity check: group public must be a valid non-zero 32-byte hex.
		if (!/^[0-9a-fA-F]{64}$/.test(outcome.publicKeyPackage.groupPublic)) {
			throw new Error(`DKG produced malformed group public: ${outcome.publicKeyPackage.groupPublic}`);
		}

		// Everyone broadcasts "done" with their group_public so drift is detected.
		for (const pid of otherPartyIds) {
			await sendMsg(transport, key, myPartyId, pid, {
				type: 'dkg-done',
				partyId: myPartyId,
				groupPublic: outcome.publicKeyPackage.groupPublic,
			});
		}

		// Drain any peer 'done' messages so we detect group-public drift
		// before returning. This is an advisory check; the per-party
		// PoK verification in round 2 already guarantees consistency.
		const peerGroupPublics: Record<string, string> = {};
		const drainDeadline = Math.min(deadline, Date.now() + 10_000);
		while (
			Object.keys(peerGroupPublics).length < otherPartyIds.length &&
			Date.now() < drainDeadline
		) {
			checkAborted(callbacks?.signal);
			await inbox.drain(transport, key, myPartyId);
			inbox.throwOnError();
			const dones = inbox.take(
				(m): m is DkgDoneMsg =>
					m.type === 'dkg-done' && !!identifiers[m.partyId],
			) as DkgDoneMsg[];
			for (const m of dones) peerGroupPublics[m.partyId] = m.groupPublic;
			if (Object.keys(peerGroupPublics).length < otherPartyIds.length) {
				await new Promise(r => setTimeout(r, POLL_ACTIVE_MS));
			}
		}
		for (const [pid, gp] of Object.entries(peerGroupPublics)) {
			if (gp.toLowerCase() !== outcome.publicKeyPackage.groupPublic.toLowerCase()) {
				throw new Error(
					`DKG desync: peer ${pid} derived group_public=${gp} but we have ${outcome.publicKeyPackage.groupPublic}`,
				);
			}
		}

		// ── Shared Orchard-extras broadcast ────────────────────────
		// Every device needs the same 64-byte nk‖rivk so their merged
		// Orchard UFVKs all match. `orchard_random_extras` requires a
		// valid `ak` (the group_public) to validate the generated FVK,
		// so we can only run it after round 3 produces the shared key.
		// The initiator generates once and broadcasts; joiners wait for
		// the broadcast. This extra round trip is cheap (one message
		// per joiner) and closes the "different UFVK per device" hole.
		callbacks?.onProgress?.('Finalising shared Orchard scan key...');
		let orchardExtras: string | undefined;
		if (isInitiator) {
			try {
				orchardExtras = generateOrchardExtrasFn(
					outcome.publicKeyPackage.groupPublic,
				);
			} catch (err) {
				throw new Error(
					'Initiator cannot generate shared Orchard extras: ' +
						(err as Error).message +
						'. Update the orchard-frost WASM bundle — a ceremony without ' +
						'a shared nk‖rivk leaves every device with a different UFVK.',
				);
			}
			for (const pid of otherPartyIds) {
				await sendMsg(transport, key, myPartyId, pid, {
					type: 'dkg-extras',
					from: myPartyId,
					orchardExtras,
				});
			}
		} else {
			const extrasDeadline = Math.min(deadline, Date.now() + 30_000);
			while (!orchardExtras && Date.now() < extrasDeadline) {
				checkAborted(callbacks?.signal);
				await inbox.drain(transport, key, myPartyId);
				inbox.throwOnError();
				const extrasMsgs = inbox.take(
					(m): m is DkgExtrasMsg =>
						m.type === 'dkg-extras' && !!m.orchardExtras,
				) as DkgExtrasMsg[];
				if (extrasMsgs.length) {
					orchardExtras = extrasMsgs[0].orchardExtras;
					break;
				}
				await new Promise(r => setTimeout(r, POLL_ACTIVE_MS));
			}
			if (!orchardExtras) {
				throw new Error(
					'Timed out waiting for initiator to broadcast shared Orchard extras. ' +
						'Retry the ceremony — without shared nk‖rivk every device would ' +
						'derive a different Orchard UFVK.',
				);
			}
		}

		return {
			myPartyId,
			myIdentifier,
			share: outcome.share,
			publicKeyPackage: outcome.publicKeyPackage,
			minSigners,
			maxSigners,
			participantPartyIds: Object.keys(identifiers),
			orchardExtras,
		};
	} finally {
		// Always free the Rust-side participant allocation — it holds
		// the round-secret polynomial coefficients and shouldn't linger.
		try { participant.free(); } catch { /* already freed */ }
	}
}

// ── Trusted-dealer ceremony (Option C — "a little less secure") ────
//
// Initiator generates all N shares locally with `orchardKeygen` (which
// uses `keygen_with_dealer` under the hood) and distributes each
// non-local share to its assigned party over the encrypted relay. The
// initiator briefly holds the full polynomial — hence "less secure" —
// but no share ever travels in plaintext, and no trust is required of
// the relay. Joiners only need to be online during the ceremony; they
// get their share "live" and don't need the other devices to be
// reachable afterwards to produce their .vult backup.

import { orchardKeygen } from '../components/toolbox/zcash-extensions/frozt/orchardFrostBridge';

export interface RunTrustedDealerInitiatorParams {
	session: DkgSessionParams;
	myPartyId: string;
	otherPartyIds: string[];
	minSigners: number;
	vaultUid?: string;
	/** Optional pre-computed Orchard extras blob (groupPublic-derived).
	 *  When present it is shipped to each joiner so they don't have to
	 *  recompute it. */
	orchardExtras?: string;
	callbacks?: DkgCeremonyCallbacks;
	transport?: CosignTransport;
}

export interface RunTrustedDealerJoinerParams {
	session: DkgSessionParams;
	myPartyId: string;
	initiatorPartyId: string;
	vaultUid?: string;
	callbacks?: DkgCeremonyCallbacks;
	transport?: CosignTransport;
}

export interface TrustedDealerResult {
	myPartyId: string;
	share: OrchardKeyShare;
	publicKeyPackage: OrchardPublicKeyPackage;
	orchardExtras?: string;
	minSigners: number;
	maxSigners: number;
	participantPartyIds: string[];
}

/**
 * Assign each partyId one of the freshly-generated shares. The keygen
 * output is keyed by FROST identifier hex (0x01..., 0x02...) in the
 * same order `assignIdentifiers` uses for parties sorted lexically.
 */
function assignSharesToParties(
	allPartyIds: string[],
	keygenShares: Record<string, OrchardKeyShare>,
): Record<string, OrchardKeyShare> {
	const identifiers = assignIdentifiers(allPartyIds);
	const out: Record<string, OrchardKeyShare> = {};
	for (const pid of allPartyIds) {
		const idHex = identifiers[pid];
		const share = keygenShares[idHex];
		if (!share) {
			throw new Error(`Trusted-dealer: no share for party ${pid} (id ${idHex})`);
		}
		out[pid] = share;
	}
	return out;
}

export async function runInitiatorTrustedDealerCeremony(
	params: RunTrustedDealerInitiatorParams,
): Promise<TrustedDealerResult> {
	const { session, myPartyId, otherPartyIds, minSigners, callbacks, orchardExtras } = params;
	const allPartyIds = [myPartyId, ...otherPartyIds];
	if (new Set(allPartyIds).size !== allPartyIds.length) {
		throw new Error('Duplicate partyId in trusted-dealer participant list');
	}
	const maxSigners = allPartyIds.length;
	if (minSigners < 2) throw new Error('minSigners must be >= 2');
	if (minSigners > maxSigners) throw new Error('minSigners must be <= maxSigners');

	const key = await importEncKey(session.encKeyHex);
	const transport = params.transport
		?? createTransport('relay', session.sessionId, session.relayUrl);
	const inbox = new CeremonyInbox();

	const deadline = Date.now() + CEREMONY_TIMEOUT_MS;

	try {
		// Phase A: wait for all other parties to join.
		callbacks?.onProgress?.(`Waiting for ${otherPartyIds.length} device(s) to join...`);
		const joined = new Set<string>();
		while (joined.size < otherPartyIds.length) {
			checkAborted(callbacks?.signal);
			if (Date.now() > deadline) throw new Error('Trusted-dealer ceremony timed out waiting for joiners');
			await inbox.drain(transport, key, myPartyId);
			inbox.throwOnError();
			const joins = inbox.take(
				(m): m is DkgJoinMsg => m.type === 'dkg-join',
			) as DkgJoinMsg[];
			for (const m of joins) {
				if (!otherPartyIds.includes(m.partyId)) continue;
				if (!joined.has(m.partyId)) {
					joined.add(m.partyId);
					callbacks?.onPartyJoined?.(m.partyId, joined.size, otherPartyIds.length);
				}
			}
			if (joined.size < otherPartyIds.length) await new Promise(r => setTimeout(r, POLL_ACTIVE_MS));
		}

		// Phase B: generate all shares locally.
		callbacks?.onProgress?.(`Generating Orchard FROST shares (${minSigners}-of-${maxSigners})...`);
		const keygen = orchardKeygen(maxSigners, minSigners);
		const sharesByParty = assignSharesToParties(allPartyIds, keygen.shares);

		// Phase C: send each joiner their share over the encrypted relay.
		callbacks?.onProgress?.('Distributing shares over encrypted relay...');
		for (const joinerPartyId of otherPartyIds) {
			checkAborted(callbacks?.signal);
			const share = sharesByParty[joinerPartyId];
			const msg: TdSharePackageMsg = {
				type: 'td-share',
				from: myPartyId,
				toPartyId: joinerPartyId,
				share,
				publicKeyPackage: keygen.publicKeyPackage,
				orchardExtras,
				minSigners,
				maxSigners,
				participantPartyIds: allPartyIds,
			};
			await sendMsg(transport, key, myPartyId, joinerPartyId, msg);
		}

		// Phase D: wait for acks from every joiner.
		callbacks?.onProgress?.('Waiting for confirmations...');
		const acked = new Set<string>();
		while (acked.size < otherPartyIds.length) {
			checkAborted(callbacks?.signal);
			if (Date.now() > deadline) throw new Error('Trusted-dealer ceremony timed out waiting for acks');
			await inbox.drain(transport, key, myPartyId);
			inbox.throwOnError();
			const acks = inbox.take(
				(m): m is TdAckMsg => m.type === 'td-ack',
			) as TdAckMsg[];
			for (const m of acks) {
				if (!otherPartyIds.includes(m.from)) continue;
				acked.add(m.from);
				callbacks?.onProgress?.(`Confirmed: ${m.from} (${acked.size}/${otherPartyIds.length})`);
			}
			if (acked.size < otherPartyIds.length) await new Promise(r => setTimeout(r, POLL_ACTIVE_MS));
		}

		callbacks?.onProgress?.('Trusted-dealer ceremony complete.');
		return {
			myPartyId,
			share: sharesByParty[myPartyId],
			publicKeyPackage: keygen.publicKeyPackage,
			orchardExtras,
			minSigners,
			maxSigners,
			participantPartyIds: allPartyIds,
		};
	} finally {
		if (!params.transport) {
			try { transport.close?.(); } catch { /* ignore */ }
		}
	}
}

export async function runJoinerTrustedDealerCeremony(
	params: RunTrustedDealerJoinerParams,
): Promise<TrustedDealerResult> {
	const { session, myPartyId, initiatorPartyId, callbacks } = params;

	const key = await importEncKey(session.encKeyHex);
	const transport = params.transport
		?? createTransport('relay', session.sessionId, session.relayUrl);
	const inbox = new CeremonyInbox();

	const deadline = Date.now() + CEREMONY_TIMEOUT_MS;

	try {
		// Announce join.
		callbacks?.onProgress?.('Announcing join to initiator...');
		await sendMsg(transport, key, myPartyId, initiatorPartyId, {
			type: 'dkg-join', partyId: myPartyId, vaultUid: params.vaultUid,
		});

		// Wait for our share.
		callbacks?.onProgress?.('Waiting for encrypted share...');
		let sharePacket: TdSharePackageMsg | null = null;
		while (!sharePacket) {
			checkAborted(callbacks?.signal);
			if (Date.now() > deadline) throw new Error('Trusted-dealer ceremony timed out waiting for share');
			await inbox.drain(transport, key, myPartyId);
			inbox.throwOnError();
			const shares = inbox.take(
				(m): m is TdSharePackageMsg =>
					m.type === 'td-share' && m.toPartyId === myPartyId,
			) as TdSharePackageMsg[];
			if (shares.length) { sharePacket = shares[0]; break; }
			await new Promise(r => setTimeout(r, POLL_ACTIVE_MS));
		}

		// Ack receipt.
		callbacks?.onProgress?.('Confirming receipt...');
		await sendMsg(transport, key, myPartyId, initiatorPartyId, {
			type: 'td-ack', from: myPartyId,
		});

		callbacks?.onProgress?.('Share received and stored.');
		return {
			myPartyId,
			share: sharePacket.share,
			publicKeyPackage: sharePacket.publicKeyPackage,
			orchardExtras: sharePacket.orchardExtras,
			minSigners: sharePacket.minSigners,
			maxSigners: sharePacket.maxSigners,
			participantPartyIds: sharePacket.participantPartyIds,
		};
	} finally {
		if (!params.transport) {
			try { transport.close?.(); } catch { /* ignore */ }
		}
	}
}

// ── Error broadcast helper (best-effort; used from catch handlers) ──

export async function broadcastDkgError(
	transport: CosignTransport,
	encKeyHex: string,
	fromPartyId: string,
	toPartyIds: string[],
	message: string,
): Promise<void> {
	try {
		const key = await importEncKey(encKeyHex);
		await Promise.all(toPartyIds.map(pid =>
			sendMsg(transport, key, fromPartyId, pid, {
				type: 'dkg-error', from: fromPartyId, message,
			}),
		));
	} catch (err) {
		console.warn('[dkg-ceremony] failed to broadcast error:', (err as Error).message);
	}
}


